import { SupabaseClient } from '@supabase/supabase-js';
import supabase from './supabase';

export interface SupabaseResponse<T> {
  data: T | null;
  error: any;
}

export interface SupabaseListResponse<T> {
  data: T[] | null;
  error: any;
  count?: number | null;
}

export interface QueryOptions {
  select?: string;
  orderBy?: string;
  ascending?: boolean;
  limit?: number;
  offset?: number;
}

export interface FilterOptions {
  column: string;
  operator: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'like' | 'ilike' | 'in' | 'is';
  value: any;
}

export interface InsertOptions {
  returning?: string;
  upsert?: boolean;
}

export interface UpdateOptions {
  returning?: string;
}

export interface DeleteOptions {
  returning?: string;
}

export abstract class SupabaseTableBase<T extends Record<string, any>> {
  protected client: SupabaseClient;
  protected tableName: string;

  constructor(tableName: string, client?: SupabaseClient) {
    this.tableName = tableName;
    this.client = client || supabase;
  }

  getTableName(): string {
    return this.tableName;
  }

  getClient(): SupabaseClient {
    return this.client;
  }

  async insertRecord(data: Partial<T>, options?: InsertOptions): Promise<SupabaseResponse<T>> {
    try {
      const { data: result, error } = await this.client
        .from(this.tableName)
        .insert(data)
        .select(options?.returning || '*')
        .single();
      
      return { data: result as unknown as T, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  async insertMultipleRecords(data: Partial<T>[], options?: InsertOptions): Promise<SupabaseListResponse<T>> {
    try {
      const { data: result, error } = await this.client
        .from(this.tableName)
        .insert(data)
        .select(options?.returning || '*');
      
      return { data: result as unknown as T[], error };
    } catch (error) {
      return { data: null, error };
    }
  }

  async insertOrUpdateRecord(data: Partial<T>, options?: InsertOptions): Promise<SupabaseResponse<T>> {
    try {
      const { data: result, error } = await this.client
        .from(this.tableName)
        .upsert(data)
        .select(options?.returning || '*')
        .single();
      
      return { data: result as unknown as T, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  async selectRecordById(id: string | number, options?: QueryOptions): Promise<SupabaseResponse<T>> {
    try {
      const { data: result, error } = await this.client
        .from(this.tableName)
        .select(options?.select || '*')
        .eq('id', id)
        .single();
      
      return { data: result as unknown as T, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  async selectRecordsByColumn(column: string, value: any, options?: QueryOptions): Promise<SupabaseListResponse<T>> {
    try {
      let query = this.client
        .from(this.tableName)
        .select(options?.select || '*')
        .eq(column, value);

      if (options?.orderBy) {
        query = query.order(options.orderBy, { ascending: options.ascending ?? true });
      }

      if (options?.limit) {
        query = query.limit(options.limit);
      }

      if (options?.offset) {
        query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
      }

      const { data: result, error } = await query;
      return { data: result as unknown as T[], error };
    } catch (error) {
      return { data: null, error };
    }
  }

  async selectAllRecords(options?: QueryOptions & { filters?: FilterOptions[] }): Promise<SupabaseListResponse<T>> {
    try {
      let query = this.client.from(this.tableName).select(options?.select || '*');
      
      if (options?.filters) {
        options.filters.forEach(filter => {
          query = query.filter(filter.column, filter.operator, filter.value);
        });
      }

      if (options?.orderBy) {
        query = query.order(options.orderBy, { ascending: options.ascending ?? true });
      }

      if (options?.limit) {
        query = query.limit(options.limit);
      }

      if (options?.offset) {
        query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
      }

      const { data: result, error } = await query;
      return { data: result as unknown as T[], error };
    } catch (error) {
      return { data: null, error };
    }
  }

  async updateRecordById(id: string | number, data: Partial<T>, options?: UpdateOptions): Promise<SupabaseResponse<T>> {
    try {
      const { data: result, error } = await this.client
        .from(this.tableName)
        .update(data)
        .eq('id', id)
        .select(options?.returning || '*')
        .single();
      
      return { data: result as unknown as T, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  async updateRecordsByColumn(column: string, value: any, data: Partial<T>, options?: UpdateOptions): Promise<SupabaseListResponse<T>> {
    try {
      const { data: result, error } = await this.client
        .from(this.tableName)
        .update(data)
        .eq(column, value)
        .select(options?.returning || '*');
      
      return { data: result as unknown as T[], error };
    } catch (error) {
      return { data: null, error };
    }
  }

  async deleteRecordById(id: string | number, options?: DeleteOptions): Promise<SupabaseResponse<T>> {
    try {
      const { data: result, error } = await this.client
        .from(this.tableName)
        .delete()
        .eq('id', id)
        .select(options?.returning || '*')
        .single();
      
      return { data: result as unknown as T, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  async deleteRecordsByColumn(column: string, value: any, options?: DeleteOptions): Promise<SupabaseListResponse<T>> {
    try {
      const { data: result, error } = await this.client
        .from(this.tableName)
        .delete()
        .eq(column, value)
        .select(options?.returning || '*');
      
      return { data: result as unknown as T[], error };
    } catch (error) {
      return { data: null, error };
    }
  }

  async countRecords(filters?: FilterOptions[]): Promise<SupabaseResponse<{ count: number }>> {
    try {
      let query = this.client
        .from(this.tableName)
        .select('*', { count: 'exact', head: true });
      
      if (filters) {
        filters.forEach(filter => {
          query = query.filter(filter.column, filter.operator, filter.value);
        });
      }

      const { count, error } = await query;
      return { data: count ? { count } : null, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  async checkRecordExistsById(id: string | number): Promise<boolean> {
    try {
      const { count } = await this.client
        .from(this.tableName)
        .select('id', { count: 'exact', head: true })
        .eq('id', id);
      
      return count ? count > 0 : false;
    } catch (error) {
      return false;
    }
  }

  async checkRecordExistsByColumn(column: string, value: any): Promise<boolean> {
    try {
      const { count } = await this.client
        .from(this.tableName)
        .select(column, { count: 'exact', head: true })
        .eq(column, value);
      
      return count ? count > 0 : false;
    } catch (error) {
      return false;
    }
  }

  async selectRecordsWithPagination(page: number = 1, limit: number = 10, options?: QueryOptions & { filters?: FilterOptions[] }): Promise<SupabaseListResponse<T> & { page: number; limit: number; totalPages: number }> {
    try {
      const offset = (page - 1) * limit;
      
      let query = this.client.from(this.tableName).select(options?.select || '*');
      
      if (options?.filters) {
        options.filters.forEach(filter => {
          query = query.filter(filter.column, filter.operator, filter.value);
        });
      }

      if (options?.orderBy) {
        query = query.order(options.orderBy, { ascending: options.ascending ?? true });
      }

      query = query.range(offset, offset + limit - 1);

      const { data: result, error } = await query;
      
      const countResult = await this.countRecords(options?.filters);
      const total = countResult.data?.count || 0;
      const totalPages = Math.ceil(total / limit);

      return {
        data: result as unknown as T[],
        error,
        page,
        limit,
        totalPages
      };
    } catch (error) {
      return { data: null, error, page, limit, totalPages: 0 };
    }
  }

  async searchRecordsByText(searchTerm: string, searchColumns: string[], options?: QueryOptions): Promise<SupabaseListResponse<T>> {
    try {
      let query = this.client.from(this.tableName).select(options?.select || '*');
      
      const searchQuery = searchColumns.map(column => `${column}.ilike.%${searchTerm}%`).join(',');
      query = query.or(searchQuery);

      if (options?.orderBy) {
        query = query.order(options.orderBy, { ascending: options.ascending ?? true });
      }

      if (options?.limit) {
        query = query.limit(options.limit);
      }

      if (options?.offset) {
        query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
      }

      const { data: result, error } = await query;
      return { data: result as unknown as T[], error };
    } catch (error) {
      return { data: null, error };
    }
  }

  async executeCustomQuery(queryBuilder: (query: any) => any): Promise<SupabaseListResponse<T>> {
    try {
      const query = this.client.from(this.tableName);
      const { data: result, error } = await queryBuilder(query);
      return { data: result as unknown as T[], error };
    } catch (error) {
      return { data: null, error };
    }
  }

  async executeRawSQL(sql: string, params?: any[]): Promise<SupabaseListResponse<T>> {
    try {
      const { data: result, error } = await this.client.rpc('execute_sql', { 
        sql_query: sql, 
        params: params || [] 
      });
      return { data: result as unknown as T[], error };
    } catch (error) {
      return { data: null, error };
    }
  }

  subscribeToTableChanges(
    callback: (payload: any) => void,
    filter?: { event?: 'INSERT' | 'UPDATE' | 'DELETE'; schema?: string; table?: string }
  ) {
    const channel = this.client
      .channel(`${this.tableName}_changes`)
      .on(
        'postgres_changes' as any,
        {
          event: filter?.event || '*',
          schema: filter?.schema || 'public',
          table: filter?.table || this.tableName,
        },
        callback
      )
      .subscribe();

    return channel;
  }

  unsubscribeFromTableChanges(channel: any) {
    this.client.removeChannel(channel);
  }
}